import { useQuery, useQueryClient } from '@tanstack/react-query';
import { streamDestinationsQueryOptions } from '../api/streamDestinations';
import { AllowedProvider, providersOrder } from '../constants/common';
import { authContextQueryOptions, channelsOwnerAccessQueryOptions } from '../api/channels';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { DestinationsStorageApiClient } from '../services/DestinationsApiClient';
import { defaultCanvasConfigurationStatusQueryOptions } from '../routes/_authenticated.index/-defaultCanvasSetup';
import { OutputId, OutputInfo } from '../types/obs';
/**
 * Returns a list of outputs to be presented. It ensures
 * Tiktok is always present in the list as per spec.
 */
export function useOutputsList({
  serviceEnvironment,
  authContextProvider,
}: {
  serviceEnvironment: ServiceEnvironment;
  authContextProvider: AuthContextProvider;
}) {
  const { data: authContext } = useQuery(
    authContextQueryOptions({
      authContextProvider,
    })
  );

  const { data: channels = [] as never[] } = useQuery(
    channelsOwnerAccessQueryOptions({
      serviceEnvironment,
      authContextProvider,
    })
  );

  const { data: destinations } = useQuery(
    streamDestinationsQueryOptions({
      authContextProvider,
      serviceEnvironment,
    })
  );

  // The following block should ensure the outputs list changes when SE.Live reports the list changed
  const [ /*updateTimestamp*/, setUpdateTimestamp ] = useState(Date.now());

  useEffect(() => {
    const handleEvent = () => {
      setUpdateTimestamp(Date.now());
    };

    window.addEventListener('hostStreamingOutputListChanged', handleEvent);

    return () => {
      window.removeEventListener('hostStreamingOutputListChanged', handleEvent);
    };
  }, [setUpdateTimestamp]);

  /*
  const channelsByProvider = useMemo(() => {
    return channels.reduce((acc, channel) => {
      const arr = acc.get(channel.provider) || [];
      arr.push(channel);
      acc.set(channel.provider, arr);
      return acc;
    }, new Map<AllowedProvider, typeof channels>());
  }, [channels]);
  */

  const listItems = useMemo(() => {
    if (!destinations) return [];

    const customDestinations = Array.from(destinations).filter(([, destination]) => {
      if (destination.provider === 'custom' || destination.provider === 'tiktok') {
        return true;
      }
      return false;
    });

    const result = [
      ...channels.map((channel) => ({
        id: channel.channelId,
        displayName: channel.username,
        provider: channel.provider as AllowedProvider,
        avatar: channel.avatar,
        username: channel.username.toLocaleLowerCase(),
        isPrimaryOutput:
          destinations?.get(channel.channelId)?.outputs.some((output) => output?.auxiliaryData?.isPrimaryOutput) ??
          false,
        isEnabled: false,
      })),
      ...customDestinations.map(([key, destination]) => ({
        id: key,
        displayName: destination.displayName || '',
        provider: destination.provider,
        avatar: '',
        username: destination.displayName || '',
        isEnabled: !!destination.isEnabled,
        isPrimaryOutput: destination?.outputs.some((output) => output.auxiliaryData?.isPrimaryOutput) ?? false,
      })),
    ].sort((a, b) => {
      // Make sure primary outputs are always at the top
      if (a.isPrimaryOutput) return -1;
      if (b.isPrimaryOutput) return 1;

      // Move the current channel to the top
      if (a.id === authContext?.channel._id) return -1;
      if (b.id === authContext?.channel._id) return 1;

      // Sort by provider order
      const aIndex = providersOrder.indexOf(a.provider as AllowedProvider);
      const bIndex = providersOrder.indexOf(b.provider as AllowedProvider);
      return aIndex - bIndex;
    });

    return result;
  }, [channels, /*channelsByProvider,*/ authContext?.channel._id, destinations]);

  return listItems;
}

export function useUnmarkPrimaryOutputOnDefaultConfigured({
  serviceEnvironment,
  authContextProvider,
}: {
  serviceEnvironment: ServiceEnvironment;
  authContextProvider: AuthContextProvider;
}) {
  const queryClient = useQueryClient();
  const {
    data: {
      isConfigured: defaultIsConfigured = false,
      isConfiguredFromCustomOutput: defaultIsConfiguredFromCustomOutput = false,
    } = {} as never,
  } = useQuery(defaultCanvasConfigurationStatusQueryOptions);
  const unmarkPrimaryOutputTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (unmarkPrimaryOutputTimeout.current) {
      clearTimeout(unmarkPrimaryOutputTimeout.current);

      unmarkPrimaryOutputTimeout.current = null;
    } 

    if (defaultIsConfiguredFromCustomOutput) {
      return;
    }

    if (!defaultIsConfigured) {
      return;
    }

    unmarkPrimaryOutputTimeout.current = setTimeout(async () => {
      const destinationsStorageApiClient = new DestinationsStorageApiClient({
        authContextProvider,
        serviceEnvironment,
      });

      await destinationsStorageApiClient.unmarkCurrentPrimaryOutput();

      queryClient.invalidateQueries(
        streamDestinationsQueryOptions({
          authContextProvider,
          serviceEnvironment,
        })
      );
    }, 250);

    return () => {
      if (unmarkPrimaryOutputTimeout.current) {
        clearTimeout(unmarkPrimaryOutputTimeout.current);
  
        unmarkPrimaryOutputTimeout.current = null;
      } 
    };
  }, [authContextProvider, defaultIsConfigured, defaultIsConfiguredFromCustomOutput, queryClient, serviceEnvironment]);
}

export function useAllObsStreamingOutputs(): Record<OutputId, OutputInfo> {
  const [ state, setState ] = useState<Record<OutputId, OutputInfo>>({} as never);

  useEffect(() => {
    const abortController = new AbortController();

    const update = () => {
      window.host.getAllStreamingOutputs((data) => {
        if (abortController.signal.aborted) return;

        setState(data);
      });
    };

    window.addEventListener('hostStreamingOutputListChanged', update, { signal: abortController.signal });

    update();

    return () => {
      abortController.abort();
    };
  }, []);

  return state;
}
